import React, { FC, ReactNode } from "react";
import { TimesEntry } from "../../Helpers/interfaces";
import classNames from "classnames";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { nb } from "date-fns/locale";
import { AppointmentDto } from "../../../../../Api/Model/AppointmentDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { ServiceDto } from "../../../../../Api/Model/ServiceDto.csharp";
import { format, startOfDay } from "date-fns";
import { RelatedServiceSetup } from "@apiModels/RelatedServiceSetup.csharp";
import { findRelatedSetupForAppointment } from "../../Helpers/relatedServiceHelper";

enum HourPickerElement {
  buttonActive = "hourPicker__button--active",
  itemActive = "hourPicker__element--active",
}

interface HourPickerSectionProps extends TimesEntry {
  selectedAppointment?: AppointmentDto;
  doctors: DoctorDto[];
  centers: LocationDto[];
  nextAvailableDateLabel: string;
  goToNextAvailableDay: (date: Date) => void;
  onSelect: (appointment: AppointmentDto) => void;
  selectedDoctor: DoctorDto;
  selectedLocation: LocationDto;
  selectedService: ServiceDto;
  relatedServiceSetups: RelatedServiceSetup[];
}

export const HourPickerSection: FC<HourPickerSectionProps> = ({
  appointments,
  label,
  nextAvailableDate,
  nextAvailableDateLabel,
  selectedAppointment,
  doctors,
  centers,
  goToNextAvailableDay,
  onSelect,
  selectedDoctor,
  selectedLocation,
  selectedService,
  relatedServiceSetups,
}) => {
  function mapDates(appointment: AppointmentDto, index: number): ReactNode {
    const active =
      appointment.startTime.getTime() ==
        selectedAppointment?.startTime?.getTime() &&
      appointment.doctorId == selectedAppointment?.doctorId &&
      appointment.serviceId == selectedAppointment?.serviceId;

    const [appointmentDoctor] = doctors!.filter(
      (doctor) =>
        doctor.isNotADoctor == false &&
        doctor.id == selectedAppointment!.doctorId
    );
    const [appointmentCenter] = centers.filter(
      (l) => l.id == selectedAppointment?.centerId
    );

    const relatedSetup = findRelatedSetupForAppointment(
      relatedServiceSetups,
      appointment
    );

    const showDetailsForRelated =
      appointment.isRelatedAppointment &&
      !(
        relatedSetup.relatedServiceLocationSetup
          ?.applyLocationAndDoctorFilter ??
        relatedSetup?.relatedServiceSetup?.applyLocationAndDoctorFilter
      );

    const displayCenterDetails =
      (!selectedLocation.id ||
        selectedLocation.id == "-1" ||
        showDetailsForRelated ||
        selectedLocation.isLocation) &&
      appointmentCenter;
    const displayDoctorDetails =
      (!selectedDoctor.id ||
        selectedDoctor.id == "-1" ||
        showDetailsForRelated) &&
      appointmentDoctor;
    const displayAdditionalInfo =
      active && (displayCenterDetails || displayDoctorDetails);
    return (
      <li
        key={`${appointment}-${index}`}
        className={classNames("hourPicker__element", {
          [HourPickerElement.itemActive]: displayAdditionalInfo,
        })}
      >
        <button
          className={classNames("hourPicker__button", {
            [HourPickerElement.buttonActive]: active,
          })}
          onClick={() => onSelect(appointment)}
          type="button"
          style={{
            background:
              appointment.isRelatedAppointment && !active
                ? relatedSetup?.relatedServiceSetup?.highlightColor
                : undefined,
          }}
        >
          {format(appointment.startTime, "HH:mm")}
        </button>
        {displayAdditionalInfo && (
          <div className="position-absolute hourPicker__info">
            {displayDoctorDetails && (
              <div>
                <span className="d-block fs-6 mt-1">
                  {appointmentDoctor.name}
                </span>
                {selectedService.showDoctorsJobTitle && (
                  <span className="fs-6 hourPicker__doctor-title">
                    {appointmentDoctor.jobTitle}
                  </span>
                )}
              </div>
            )}
            {displayCenterDetails && (
              <span
                className={classNames("d-block fs-6", {
                  " mt-1": !displayDoctorDetails,
                })}
              >
                {appointmentCenter.name}
              </span>
            )}
          </div>
        )}
      </li>
    );
  }

  const getNextAvailableLabel = () => {
    const index = nextAvailableDateLabel.indexOf("$date");
    const firstPart = nextAvailableDateLabel.substring(0, index);
    const secondPart = nextAvailableDateLabel.substring(index + 6);

    const date = startOfDay(nextAvailableDate!);
    return (
      <>
        {firstPart}
        <button
          className="next-available-button"
          onClick={() => goToNextAvailableDay(date)}
          type="button"
        >
          {format(date, "EEEE dd. MMMM", { locale: nb })}
        </button>
        {secondPart}
      </>
    );
  };

  if (appointments.length == 0 && !nextAvailableDate) return null;

  return (
    <>
      <h3 className="d-block text-center mt-2 fs-6">{label}</h3>
      {appointments.length != 0 ? (
        <ul className="d-flex flex-wrap justify-content-start p-0 hourPicker__list">
          {appointments.map(mapDates)}
        </ul>
      ) : (
        nextAvailableDate && (
          <p className="text-center">{getNextAvailableLabel()}</p>
        )
      )}
    </>
  );
};
